export type CrmMapViewId =
  | 'overview'
  | 'lifecycle'
  | 'roles'
  | 'data'
  | 'domains'
  | 'integrations';

export const CRM_MAP_VIEWS: { id: CrmMapViewId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'lifecycle', label: 'Customer lifecycle' },
  { id: 'roles', label: 'Roles & routes' },
  { id: 'data', label: 'Data model' },
  { id: 'domains', label: 'Feature domains' },
  { id: 'integrations', label: 'Integrations' },
];

export type LifecycleNode = {
  id: string;
  label: string;
  collection?: string;
  route?: string;
  file?: string;
  detail: string;
  isBackEdgeTarget?: boolean;
};

export const LIFECYCLE_NODES: LifecycleNode[] = [
  {
    id: 'lead',
    label: 'Lead created',
    collection: 'leads',
    route: '/leads/new',
    file: 'src/pages/LeadForm.tsx',
    detail:
      'New leads enter Firestore via forms, imports, or Cloud Function createLead. CRMContext listens in real time.',
  },
  {
    id: 'map',
    label: 'MapDashboard',
    route: '/',
    file: 'src/pages/MapDashboard.tsx',
    detail:
      'Employee home: geo map, list view, and deal pipeline tabs. Primary entry for sales discovery.',
  },
  {
    id: 'clientWS',
    label: 'Client workspace',
    route: '/client/:leadId',
    file: 'src/pages/ClientPortalManagement.tsx',
    detail:
      'Central sales UI per lead: portal stages, proposals, comms, documents, and project naming.',
    isBackEdgeTarget: true,
  },
  {
    id: 'engagement',
    label: 'Engagement letter',
    collection: 'engagementLetters',
    route: '/engagement/:slug',
    file: 'src/components/engagement/EngagementLettersList.tsx',
    detail:
      'Signed engagement creates or links a clientPortals doc with slug and PIN for stakeholder access.',
  },
  {
    id: 'deal',
    label: 'EnhancedDeal',
    collection: 'enhancedDeals',
    file: 'src/utils/CRMContext.tsx',
    detail:
      'Pipeline deal linked to leadId. convertLeadToDeal and moveDealToStage drive stage transitions.',
  },
  {
    id: 'builder',
    label: 'Proposal builder',
    collection: 'proposalBuilders/{dealId}',
    route: '/proposal-builder/:dealId',
    file: 'src/pages/ProposalBuilderPage.tsx',
    detail:
      'Page-level Firestore listener (not CRMContext). Structures, costing, versions, and PDF export.',
  },
  {
    id: 'opsRev',
    label: 'Ops review',
    collection: 'proposalBuilders/.../opsReview',
    file: 'src/utils/proposalOpsReview.ts',
    detail:
      'Internal review workflow with comments and notifications. Deep links back to proposal builder.',
  },
  {
    id: 'public',
    label: 'Public proposal',
    route: '/proposal/:slug',
    file: 'src/pages/PublicProposalPage.tsx',
    detail:
      'Client-facing sign/view flow outside CRMProvider. Uses slug + PIN; no staff auth required.',
  },
  {
    id: 'sign',
    label: 'Client signs',
    file: 'src/utils/signedDealSync.ts',
    detail:
      'Signature triggers syncSignedDealSideEffects: won deal, sales order, timber project, onboarding.',
  },
  {
    id: 'timber',
    label: 'TimberProject',
    collection: 'timberProjects',
    route: '/project-hub/:projectId',
    file: 'src/pages/ProjectHub.tsx',
    detail:
      'Post-win delivery record. Tasks, Gantt, files, time entries, and PDF annotations live here.',
  },
  {
    id: 'release',
    label: 'File release',
    collection: 'portalFileReleases',
    file: 'src/utils/portalFileReleaseService.ts',
    detail:
      'Callable releaseProjectFileToPortal pushes PM files to the client portal subcollection.',
  },
  {
    id: 'splash',
    label: 'Client portal',
    route: '/portal/:slug',
    file: 'src/pages/ClientPortalSplash.tsx',
    detail:
      'External stakeholder timeline: design fee, model, quote, production, delivery stages.',
  },
  {
    id: 'notif',
    label: 'Notifications',
    collection: 'userNotifications',
    route: '/notifications',
    file: 'src/utils/notificationDeepLinks.ts',
    detail:
      'In-app inbox fans out from Cloud Function triggers. Deep links route to client workspace, builder, or project hub.',
  },
];

export const LIFECYCLE_EDGES: { from: string; to: string; backEdge?: boolean }[] = [
  { from: 'lead', to: 'map' },
  { from: 'map', to: 'clientWS' },
  { from: 'clientWS', to: 'engagement' },
  { from: 'clientWS', to: 'deal' },
  { from: 'deal', to: 'builder' },
  { from: 'builder', to: 'opsRev' },
  { from: 'opsRev', to: 'public' },
  { from: 'public', to: 'sign' },
  { from: 'sign', to: 'timber' },
  { from: 'timber', to: 'release' },
  { from: 'release', to: 'splash' },
  { from: 'splash', to: 'notif' },
  { from: 'notif', to: 'clientWS', backEdge: true },
];

export type LifecycleLayoutNode = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

export type LifecycleLayoutEdge = {
  from: string;
  to: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  backEdge?: boolean;
};

const MAIN_SPINE = [
  'lead',
  'map',
  'clientWS',
  'deal',
  'builder',
  'opsRev',
  'public',
  'sign',
  'timber',
  'release',
  'splash',
  'notif',
];

export function computeLifecycleLayout(): {
  nodes: LifecycleLayoutNode[];
  edges: LifecycleLayoutEdge[];
  width: number;
  height: number;
} {
  const nodeWidth = 176;
  const nodeHeight = 40;
  const gapY = 52;
  const mainX = 24;
  const branchX = 236;
  const padding = 20;

  const positions = new Map<string, { x: number; y: number }>();

  MAIN_SPINE.forEach((id, index) => {
    positions.set(id, { x: mainX, y: padding + index * gapY });
  });

  const clientIdx = MAIN_SPINE.indexOf('clientWS');
  positions.set('engagement', { x: branchX, y: padding + clientIdx * gapY });

  const nodes: LifecycleLayoutNode[] = LIFECYCLE_NODES.map((node) => {
    const pos = positions.get(node.id) ?? { x: mainX, y: padding };
    return { id: node.id, x: pos.x, y: pos.y, width: nodeWidth, height: nodeHeight };
  });

  const nodeById = new Map(nodes.map((n) => [n.id, n]));

  const edges: LifecycleLayoutEdge[] = LIFECYCLE_EDGES.map((edge) => {
    const from = nodeById.get(edge.from)!;
    const to = nodeById.get(edge.to)!;
    const fromCenterX = from.x + from.width / 2;
    const toCenterX = to.x + to.width / 2;

    const isBranch = edge.from === 'clientWS' && edge.to === 'engagement';
    const isBack = edge.backEdge;

    let x1 = fromCenterX;
    let y1 = from.y + from.height;
    let x2 = toCenterX;
    let y2 = to.y;

    if (isBranch) {
      x1 = from.x + from.width;
      y1 = from.y + from.height / 2;
      x2 = to.x;
      y2 = to.y + to.height / 2;
    } else if (isBack) {
      x1 = from.x;
      y1 = from.y + from.height / 2;
      x2 = to.x + to.width;
      y2 = to.y + to.height / 2;
    }

    return { ...edge, x1, y1, x2, y2 };
  });

  const maxY = Math.max(...nodes.map((n) => n.y + n.height));
  const maxX = Math.max(...nodes.map((n) => n.x + n.width));

  return {
    nodes,
    edges,
    width: maxX + padding,
    height: maxY + padding,
  };
}

export function lifecycleNodeById(id: string): LifecycleNode | undefined {
  return LIFECYCLE_NODES.find((n) => n.id === id);
}

export const STACK_LAYERS = [
  {
    label: 'AuthProvider',
    sub: 'src/utils/AuthContext.tsx',
    desc: 'Firebase Auth + users/{uid} role resolution',
  },
  {
    label: 'CRMProvider',
    sub: 'src/utils/CRMContext.tsx',
    desc: '~25 Firestore onSnapshot listeners on mount',
  },
  {
    label: 'Pages & routes',
    sub: 'src/App.tsx',
    desc: '70+ pages, role-gated; public routes skip CRMProvider',
  },
  {
    label: 'Firestore + Storage',
    sub: 'firestore.rules',
    desc: 'Direct SDK reads/writes from browser',
  },
  {
    label: 'Cloud Functions',
    sub: 'functions/src/index.ts',
    desc: '~90 exports: webhooks, secrets, triggers',
  },
];

export const THREE_HUBS = [
  {
    title: 'Sales hub',
    route: '/ + /client/:leadId',
    files: 'MapDashboard.tsx, ClientPortalManagement.tsx',
  },
  {
    title: 'Delivery hub',
    route: '/project-hub, /operations, /shop-schedule',
    files: 'ProjectHub.tsx, OperationsHub.tsx, ProductionSchedulePage.tsx',
  },
  {
    title: 'External hub',
    route: '/portal/:slug, /proposal/:slug, /engagement/:slug',
    files: 'ClientPortalSplash.tsx, PublicProposalPage.tsx (no CRMProvider)',
  },
];

export const ROLE_ROWS: [string, string, string][] = [
  ['employee / admin', '/ → MapDashboard', 'Full CRM: leads, pipeline, proposals, analytics, People Hub, vendors'],
  ['staff', '/project-hub', 'Project Hub, shop schedule, Operations Hub only'],
  ['architect / designer', '/designer-portal', 'Design Portal + Project Hub + shop schedule'],
  ['engineer', '/designer-portal', 'Design Portal (Engineer Work) + Project Hub + shop schedule'],
  ['supplier', '/vendor-portal', 'RFQ portal scoped by supplierOrgId'],
  ['client', 'Dashboard', 'Legacy client dashboard, project view, portal page'],
];

export const PUBLIC_ROUTES: [string, string, string][] = [
  ['/engagement/:slug', 'EngagementPage', 'Engagement letter signing'],
  ['/proposal/:slug', 'PublicProposalPage', 'Public proposal view/sign'],
  ['/portal/:slug', 'ClientPortalSplash', 'Multi-step client portal'],
  ['/portal/:slug/questionnaire', 'DesignQuestionnairePage', 'Design questionnaire'],
  ['/questionnaire', 'QuestionnaireLandingPage', 'Public questionnaire signup + style guide'],
  ['/structural-styles', 'StructuralStylesPage', 'Public structural / truss style gallery (no login)'],
  ['/journey', 'JourneyPage', 'Customer journey content'],
  ['/shed-calc', 'ShedCostCalculator', 'Hidden calculator tool'],
];

export const COLLECTION_ROWS: [string, string, string, string][] = [
  ['Sales', 'leads', 'CRMContext', 'Lead list; testLeads in dev'],
  ['Sales', 'enhancedDeals', 'CRMContext', 'Primary pipeline deals'],
  ['Sales', 'pipelines', 'CRMContext', 'Stage definitions'],
  ['Sales', 'activities', 'useActivities', 'Calls, notes, system events (scoped listeners)'],
  ['Sales', 'contacts', 'CRMContext', 'CRM contacts'],
  ['Proposals', 'proposalBuilders/{dealId}', 'Page-level', 'Builder data; versions + opsReview subcollections'],
  ['Proposals', 'salesOrders', 'Page-level', 'Created on proposal sign'],
  ['Proposals', 'proposalDownloads', 'Page-level', 'Download audit trail'],
  ['Portal', 'clientPortals', 'Page-level', 'Slug, PIN, multi-project stages'],
  ['Portal', 'portalActivity', 'Page-level', 'Stakeholder activity log'],
  ['Portal', 'designQuestionnaires', 'Page-level', 'Public questionnaire responses'],
  ['Portal', 'engagementLetters', 'CRMContext', 'Engagement letter docs'],
  ['PM', 'timberProjects', 'CRMContext', 'Project Hub projects'],
  ['PM', 'projectTasks', 'CRMContext', 'Kanban/Gantt tasks'],
  ['PM', 'operationsTasks', 'Page-level', 'Ops Hub tasks + comments subcollection'],
  ['PM', 'projectOnboarding', 'CRMContext', 'Post-win onboarding checklists'],
  ['Vendors', 'rfqProjects', 'CRMContext', 'RFQ project records'],
  ['Vendors', 'rfqPackages', 'CRMContext', 'RFQ packages per project'],
  ['Vendors', 'supplierOrgs', 'CRMContext', 'Supplier organizations'],
  ['People', 'timeOffRequests', 'Page-level', 'Leave requests'],
  ['People', 'handbookPolicies', 'Page-level', 'Company handbook'],
  ['People', 'peopleTravelTrips', 'Page-level', 'Long-distance trip planning'],
  ['Marketing', 'utmStrategies', 'CRMContext', 'UTM campaign builder'],
  ['Marketing', 'metaAdInsights', 'Page-level', 'Meta ads cache'],
  ['Comms', 'smsMessages', 'CRMContext', 'Sendblue SMS log'],
  ['Comms', 'userNotifications', 'Page-level', 'Per-user notification inbox'],
  ['Finance', 'payments', 'CRMContext', 'Payment records'],
  ['Finance', 'qbExpenses', 'Page-level', 'QuickBooks expense cache'],
];

export type CrmMapDomain = {
  id: string;
  title: string;
  pages: string;
  collections: string;
  utils: string;
  connects: string;
};

export const CRM_MAP_DOMAINS: CrmMapDomain[] = [
  {
    id: 'leads',
    title: 'Leads & pipeline',
    pages: 'MapDashboard, LeadForm, DesignTracker',
    collections: 'leads, enhancedDeals, pipelines, activities (scoped via useActivities)',
    utils: 'CRMContext, pipelineStageUtils',
    connects: 'Client workspace, proposals, map markers',
  },
  {
    id: 'clientWS',
    title: 'Client workspace',
    pages: 'ClientPortalManagement (/client/:leadId)',
    collections: 'clientPortals, engagementLetters, lead subcollections',
    utils: 'portalStageProgress, portalProjectView',
    connects: 'Proposals, portal, notifications, comms',
  },
  {
    id: 'proposals',
    title: 'Proposals',
    pages: 'ProposalBuilderPage, PublicProposalPage, ProposalsSummaryPage',
    collections: 'proposalBuilders, salesOrders',
    utils: 'signedDealSync, proposalOpsReview, proposalVersioning',
    connects: 'Deals, portal stages, Project Hub on sign',
  },
  {
    id: 'portal',
    title: 'Client portal',
    pages: 'ClientPortalSplash, DesignQuestionnairePage',
    collections: 'clientPortals, portalActivity, designQuestionnaires',
    utils: 'stakeholderAuth, portalFileReleaseService',
    connects: 'Project Hub file releases, Stripe design fee',
  },
  {
    id: 'pm',
    title: 'Project delivery',
    pages: 'ProjectHub, OperationsHub, ProductionSchedulePage',
    collections: 'timberProjects, projectTasks, operationsTasks',
    utils: 'projectFileService, taskSchedule, onboardingTaskBridge',
    connects: 'Portal releases, architect/engineer portals, shop',
  },
  {
    id: 'shop',
    title: 'Shop / production',
    pages: 'ProductionSchedulePage, ShopManagementPage, ShopBigScreenPage',
    collections: 'shop_catalog, timber_kits',
    utils: 'shopFirestore',
    connects: 'Project Hub timelines, kit builder',
  },
  {
    id: 'vendors',
    title: 'Vendors / RFQ',
    pages: 'VendorManagementPage, VendorPortalPage, SupplierRfqPortalPage',
    collections: 'rfqProjects, rfqPackages, supplierOrgs',
    utils: 'vendorFileReleaseService',
    connects: 'Project files, supplier JWT claims',
  },
  {
    id: 'comms',
    title: 'Communications',
    pages: 'MessagesPage, InboxPage, GmailCallback',
    collections: 'smsMessages, emailMessages',
    utils: 'sendblue.ts, gmailApi.ts',
    connects: 'Lead workspace, Cloud Functions webhooks',
  },
  {
    id: 'marketing',
    title: 'Marketing',
    pages: 'MarketingHubLayout, MetaInsightsPage',
    collections: 'utmStrategies, metaAdInsights, marketingContent',
    utils: 'marketingService.ts',
    connects: 'Lead source tracking, analytics dashboard',
  },
  {
    id: 'finance',
    title: 'Finance',
    pages: 'FinancesHub, ProjectPLSection',
    collections: 'payments, qbExpenses, salesOrders',
    utils: 'projectPL.ts, qbTransactionLinks.ts',
    connects: 'QuickBooks webhooks, proposal sign → invoice',
  },
  {
    id: 'people',
    title: 'People Hub',
    pages: 'PeopleHubLayout, benefits, handbook, travel',
    collections: 'timeOffRequests, handbookPolicies, peopleTravelTrips',
    utils: 'peopleHubFirestore.ts, peopleTravelFirestore.ts, travelAgentService.ts',
    connects: 'travelAgentApi + Calendar Agent (executiveCalendar*)',
  },
  {
    id: 'notifications',
    title: 'Notifications',
    pages: 'NotificationCenter, NotificationsPage',
    collections: 'userNotifications',
    utils: 'notificationDeepLinks.ts',
    connects: 'All domains via CF triggers and @mentions',
  },
];

export const INTEGRATION_ROWS: [string, string, string][] = [
  ['Stripe', 'DesignFeeSignModal, shop checkout', 'createDesignFeeCheckout, designFeeWebhook, createShopKitCheckout'],
  ['Sendblue (SMS)', 'MessagesPage, sendblue.ts', 'sendSMS, sendblueWebhook, group CRUD'],
  ['Gmail', 'InboxPage, gmailApi.ts', 'exchangeGmailCode, syncGmailEmails, sendGmailEmail'],
  ['Google Calendar', 'CalendarPage, SettingsPanel', 'exchangeGoogleCode, executiveCalendar* callables'],
  ['QuickBooks', 'FinancesHub, ProjectPLSection', 'exchangeQuickBooksCode, quickbooksPaymentWebhook, createQuickBooksInvoice'],
  ['Meta Ads', 'MetaInsightsPage', 'syncMetaAdInsights, scheduledMetaSync'],
  ['Google Analytics 4', 'AnalyticsDashboard', 'getAnalytics'],
  ['Metrics Lite', 'Lead enrichment fields', 'onLeadCreated trigger enrichment'],
  ['Slack', 'Task/comment notifications', 'slackBotHandler, trigger handlers'],
  ['Gemini AI', 'aiService.ts, AIAdvisorPanel', 'geminiProxy'],
  ['Nodemailer SMTP', 'Proposal/invite emails', 'sendProposalEmail, sendKickOffEmail, send*Invite'],
  ['reCAPTCHA', 'Public lead forms', 'verifyRecaptcha'],
  ['Geocoding', 'Lead address fields', 'batchGeocode, createLead inline'],
];

export function isValidCrmMapView(value: string | null): value is CrmMapViewId {
  return CRM_MAP_VIEWS.some((v) => v.id === value);
}
